/**
 * Format a date using the Intl.DateTimeFormat API with flexible locale support.
 *
 * @param date - The date to format (Date object, timestamp, or ISO string)
 * @param options - Formatting options
 * @param options.dateStyle - Date format style: 'short', 'medium', 'long', 'full'
 * @param options.timeStyle - Time format style: 'short', 'medium', 'long', 'full'
 * @param options.locale - Locale for formatting (default: 'id-ID')
 * @param options.timezone - Timezone (default: system timezone)
 * @param options.replaceCharacters - Characters to replace in output (e.g., { '.': ':' })
 *
 * @example
 * formatDate(new Date(), { dateStyle: 'short' }) // '31/12/2021'
 * formatDate(new Date(), { dateStyle: 'long' }) // '31 Desember 2021'
 * formatDate(new Date(), { dateStyle: 'long', timeStyle: 'short' }) // '31 Desember 2021 14:30'
 * formatDate(new Date(), { dateStyle: 'short', locale: 'en-US' }) // '12/31/2021'
 * formatDate(1640995200000, { dateStyle: 'medium' }) // '31 Des 2021'
 */
export function formatDate(
  date: Date | number | string,
  options: {
    dateStyle?: 'short' | 'medium' | 'long' | 'full'
    timeStyle?: 'short' | 'medium' | 'long' | 'full'
    locale?: string
    timezone?: string
    replaceCharacters?: Record<string, string>
  } = {},
): string {
  const { dateStyle = 'short', timeStyle, locale = 'id-ID', timezone, replaceCharacters } = options

  // Convert to Date object if needed
  const dateObj = date instanceof Date ? date : new Date(date)

  // Validate date
  if (isNaN(dateObj.getTime())) {
    throw new Error('Invalid date provided')
  }

  const formatOptions: Intl.DateTimeFormatOptions = {
    dateStyle,
    timeStyle,
    timeZone: timezone,
  }

  let formatted = new Intl.DateTimeFormat(locale, formatOptions).format(dateObj)

  // Apply character replacements if provided
  if (replaceCharacters) {
    Object.entries(replaceCharacters).forEach(([from, to]) => {
      formatted = formatted.replace(new RegExp(from.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), to)
    })
  }

  return formatted
}

/**
 * Format date with common presets for Indonesian locale.
 *
 * @example
 * formatDateID(new Date(), 'short') // '31/12/2021'
 * formatDateID(new Date(), 'long') // '31 Desember 2021'
 * formatDateID(new Date(), 'long', true) // '31 Desember 2021 14:30'
 */
export function formatDateID(date: Date | number | string, type: 'short' | 'long', withTime = false): string {
  return formatDate(date, {
    dateStyle: type,
    timeStyle: withTime && type === 'long' ? 'short' : undefined,
    locale: 'id-ID',
    replaceCharacters: type === 'long' && withTime ? { '.': ':' } : undefined,
  })
}
